/**
 * UnitInput Component implemented with Material UI
 * 
 * This component provides a specialized input for numerical values with units,
 * featuring a text input and a spin input for adjusting values.
 */
import React from 'react';
import { Box, TextField, styled, TextFieldProps, BoxProps } from '@mui/material';
import { isInError } from 'reduxModules/ducks/ui';
import { useSelector } from 'react-redux';
import { capitalize, toNumber } from 'lodash';
import { UnitInputProps } from 'types/components/atoms';
import { RootState } from 'types/redux/state';
import { IsInError } from 'types/redux/selectors';

// Interface for styled wrapper component props
interface WrapperProps extends BoxProps {
  isNav?: boolean;
  error?: boolean;
}

/**
 * UnitInput component
 * 
 * @param props - Component props
 * @returns A number input with unit handling capabilities
 */
const UnitInput: React.FC<UnitInputProps> = (props) => {
  console.log('⌨️ UnitInput rendered:', {
    name: props.name,
    valueString: props.valueString,
    value: props.value,
    placeholder: props.placeholder
  });
  
  const {
    valueString,
    value,
    placeholder,
    name,
    isNav,
    setValueString,
    setValue,
    handleBlur,
    handleKeyDown,
    spinStep,
    ...otherProps
  } = props;

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    console.log('🔤 UnitInput handleChange triggered:', {
      name: props.name,
      newValue: evt.target.value,
      currentValue: valueString,
      event: evt.type
    });
    setValueString(evt.target.value);
  };

  const handleSpin = (evt: React.ChangeEvent<HTMLInputElement>) => {
    console.log('🔄 UnitInput handleSpin triggered:', {
      name: props.name,
      value: evt.target.value,
      event: evt.type
    });
    const newValue = evt.target.value;
    setTimeout(() => {
      setValue(toNumber(newValue));
    }, 75, newValue);
  };

  // Type assertion for isInError selector
  const typedIsInError = isInError as IsInError;
  const inError = useSelector((state: RootState) => typedIsInError(state, props));

  return (
    <Wrapper isNav={isNav} error={inError}>
      <TextInput
        value={valueString}
        placeholder={placeholder && name ? capitalize(name) : undefined}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(evt) => {
          console.log('🖱️ UnitInput clicked:', {
            name: props.name,
            event: evt.type,
            target: evt.target,
            currentTarget: evt.currentTarget
          });
        }}
        onFocus={(evt) => {
          console.log('🎯 UnitInput focused:', {
            name: props.name,
            event: evt.type,
            target: evt.target
          });
        }}
        variant="standard"
        InputProps={{
          disableUnderline: true,
        }}
      />
      <SpinInput
        type="number"
        value={value}
        onChange={handleSpin}
        inputProps={{
          step: spinStep,
          tabIndex: -1,
        }}
        variant="standard"
        InputProps={{
          disableUnderline: true,
        }}
      />
    </Wrapper>
  );
};

// Styled components using MUI styled API
const Wrapper = styled(Box, {
  shouldForwardProp: (prop) => !['isNav', 'error'].includes(prop as string),
})<WrapperProps>(({ theme, isNav, error }) => ({
  backgroundColor: isNav ? '#ffffff' : '#f5f5f5',  // White background for nav sections
  width: '100%',
  maxWidth: isNav ? '100%' : '200px',
  height: '40px',
  display: 'inline-block',
  border: error ? '2px solid #dc3545' : '1px solid #9e9e9e',
  borderRadius: '8px',
  padding: '6px 12px',
  boxSizing: 'border-box',
}));

const TextInput = styled(TextField)<TextFieldProps>(({ theme }) => ({
  float: 'left',
  width: '75%',
  height: '100%',
  '& .MuiInputBase-root': {
    border: 'none',
    borderRadius: '0',
    backgroundColor: 'transparent !important',
    height: '100%',
    fontSize: '16px',
    fontWeight: '500',
    color: '#2d2d2d !important',
  },
  '& .MuiInputBase-input': {
    minWidth: '0px',
    padding: '0',
    height: '100%',
    fontSize: '16px',
    fontWeight: '500',
    color: '#2d2d2d !important',
    backgroundColor: 'transparent !important',
    '&::placeholder': {
      opacity: 0.6,
      color: '#999',
    },
  },
}));

const SpinInput = styled(TextField)<TextFieldProps>(({ theme }) => ({
  width: '20px',
  float: 'right',
  height: '100%',
  '& .MuiInputBase-root': {
    border: 'none',
    borderRadius: '0',
    backgroundColor: 'transparent !important',
    height: '100%',
  },
  '& .MuiInputBase-input': {
    color: 'transparent !important',
    minWidth: '0px',
    padding: '0 16px 0 0',
    height: '100%',
    backgroundColor: 'transparent !important',
    WebkitTextFillColor: 'transparent',
    fontSize: '16px',
  },
  '& input[type="number"]::-webkit-outer-spin-button': {
    opacity: 1,
    height: '100%',
    cursor: 'pointer',
    WebkitAppearance: 'inner-spin-button',
  },
  '& input[type="number"]::-webkit-inner-spin-button': {
    opacity: 1,
    height: '100%',
    cursor: 'pointer',
    WebkitAppearance: 'inner-spin-button',
  },
}));

export type { UnitInputProps };
export default UnitInput;
